import { Component, OnInit, OnDestroy } from '@angular/core';
import { Router, Event, NavigationEnd, RouterEvent } from "@angular/router";
import { Subscription } from "rxjs";

import { AuthService } from "../../../services/auth.service";
import { LunaClaimValues } from "../../../models/user-identity.model";
import { RouteStrategy } from "../../../core/models/routing.model";
import { SubHeaderService } from "../../../core/services/sub-header.service";
import { SubHeader } from "../../../core/models/sub-header.model";
import { Dialog, DialogButton } from "../../../core/models/dialog.model";
import { DynamicDialogService } from "../../../core/services/dynamic-dialog.service";
import { SidePanelService } from "../../../common/services/side-panel.service";
import { LoaderService } from "../../../common/services/loader.service";
import { SidePanel, SidePanelLayout } from "../../../common/models/side-panel.model";
import { HeightWidthService, IHeightWidth } from "../../../common/services/height-width.service";
import { HelpLibraryService } from "../../services/help-library.service";
import { HelpNavigationItem, HelpItem, HelpCategory } from "../../models/help-library.model";


@Component({
    selector: 'app-help-library-home',
    templateUrl: './help-library-home.component.html',
    styleUrls: ['./help-library-home.component.scss'],
    standalone: false
})
export class HelpLibraryHomeComponent implements OnInit, OnDestroy {

    activeHelpItem: HelpItem | undefined;
    adminSubheader = new SubHeader({ title: "Help Library", buttons: [{ name: "Add Help Topic", method: () => this.addHelpTopic() }] });
    viewOnlySubheader = new SubHeader({ title: "Help Library" });

    private _isPhoneBP: boolean = false;    

    claims = LunaClaimValues;

    deleteConfirmDialog: Dialog = new Dialog("Are you sure you want to delete this help topic?", [new DialogButton("Yes", true, () => this.onDeleteConfirm()), new DialogButton("No", false, () => this.onDialogClose())]);
    errorDialog: Dialog = new Dialog("An error has occurred.", [new DialogButton("OK", true, () => this.onDialogClose())]);
    helpDeletedDialog: Dialog = new Dialog("Help Topic has been deleted.", [new DialogButton("OK", true, () => this.onHelpDeleted())]);

    helpNavigation: SidePanel = new SidePanel({
        tabs: [{ title: "Navigate" }],
        sections: [{ componentName: "HelpLibraryNavigation" }],
        sidePanelLayout: <SidePanelLayout>{ right: false, thin: true, constrainToScrolling: true, forceSingleTab: true, showTabsPhoneOnly: true, hostPadding: false },
        visible: false
    });

    isEditingHelp: boolean = false;

    notImplementedDialog: Dialog = new Dialog("This function has not yet been implemented.", [new DialogButton("Sorry!", true, () => this.onDialogClose())]);
    subscription: Subscription = new Subscription();
  

    constructor(
        public router: Router,
        readonly _authService: AuthService,
        readonly _subheaderService: SubHeaderService,
        readonly _sidePanelService: SidePanelService,
        readonly _heightWidthService: HeightWidthService,
        readonly dialogService: DynamicDialogService,
        readonly loader: LoaderService,
        private readonly _helpLibraryService: HelpLibraryService) {
        //hack to fix another hack in view-dashboard.component.ts that prevents reusing of route. this allows the page
        //to load new data without reloading the page
        this.router.routeReuseStrategy = new RouteStrategy();
    }

    ngOnInit() {
        let activeSubheader: SubHeader = this.viewOnlySubheader;
        if (this._authService.hasSystemPermission(LunaClaimValues.EditHelpLibrary)) {
            activeSubheader = this.adminSubheader;
        }
        this._subheaderService.updateSubHeader(activeSubheader);
        this._sidePanelService.newSidePanel(this.helpNavigation);
        this._sidePanelService.toggleSidePanel(!this._isPhoneBP);       

        let hw$ = this._heightWidthService.getHW().pipe().subscribe((hw: IHeightWidth) => {
            this._isPhoneBP = hw.isBelowMediumBP;
        });
        this.subscription.add(hw$);

        let routerSubs$ = this.router.events.subscribe(
            (event: Event | RouterEvent) => {
                if (event instanceof NavigationEnd) {
                    this.setIsEditing(event.url);
                }
            },
            (error) => { console.error(error); }
        );
        this.subscription.add(routerSubs$);

        let activeItem$ = this._helpLibraryService.getActiveNavigationItem().pipe().subscribe(
            (item) => {
                console.log("updating active nav item");
                if (item != null) {
                    this.activeHelpItem = new HelpItem();
                    this.activeHelpItem.id = item.id;
                    this.activeHelpItem.categoryId = item.categoryId;
                } else {
                    this.activeHelpItem = undefined;
                } },
            (error) => { console.error(error); }
        );
        this.subscription.add(activeItem$);
    }

    ngOnDestroy() {
        if (this.subscription) this.subscription.unsubscribe();
        this._sidePanelService.toggleSidePanel(false);
        this.router.routeReuseStrategy.shouldReuseRoute = () => false;
    }


    addHelpTopic(): void {
        this.router.navigateByUrl("help-library/create");
    }

    deleteHelp(): void {
        this.deleteConfirmDialog.toggleDialog(true);
        this.dialogService.updateDialog(this.deleteConfirmDialog);
    }

    editHelp(id: string, categoryId: string): void {
        this.router.navigateByUrl(`help-library/${categoryId}/${id}/edit`);
    }

    notImplemented(): void {
        this.notImplementedDialog.toggleDialog(true);
        this.dialogService.updateDialog(this.notImplementedDialog);
    }

    onDeleteConfirm(): void {
        if (this.activeHelpItem) {
            this.activeHelpItem.isDeleted = true;
            this.loader.toggleLoader(true);
            let deleteSubs$ = this._helpLibraryService.deleteHelpItem(this.activeHelpItem).pipe()
                .subscribe(
                    (data) => {
                        this._helpLibraryService.setActiveNavItem(null);
                        this.loader.toggleLoader(false);
                        this.helpDeletedDialog.toggleDialog(true);
                        this.dialogService.updateDialog(this.helpDeletedDialog);
                    },
                    (error) => {
                        console.error(error);                       
                        this.loader.toggleLoader(false);
                        this.errorDialog.toggleDialog(true);
                        this.dialogService.updateDialog(this.errorDialog);
                    }
            );
            this.subscription.add(deleteSubs$);
        }
    }

    onDialogClose(): void { }

    onHelpDeleted(): void {        
        this.router.navigate(["help-library"]);
    }

    setIsEditing(url: string): void {
        this.isEditingHelp = url.indexOf("edit") != -1;
    }
}
